import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { HelpCircle, Mail, MessageCircle, FileText, ExternalLink } from "lucide-react";

interface HelpModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function HelpModal({ open, onOpenChange }: HelpModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-primary" />
            Help & Support
          </DialogTitle>
          <DialogDescription>Get help with DiscoRun AI</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <a
            href="mailto:support@discorun.ai"
            className="flex items-center gap-4 p-4 bg-muted rounded-lg hover:bg-muted/80 transition-colors"
          >
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Mail className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-foreground">Email Support</h4>
              <p className="text-sm text-muted-foreground">support@discorun.ai</p>
            </div>
            <ExternalLink className="w-4 h-4 text-muted-foreground" />
          </a>

          <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
            <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-success" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-foreground">Live Chat</h4>
              <p className="text-sm text-muted-foreground">Available Mon-Fri, 9am-5pm EST</p>
            </div>
            <Button size="sm" variant="outline">
              Start Chat
            </Button>
          </div>

          <a
            href="#"
            className="flex items-center gap-4 p-4 bg-muted rounded-lg hover:bg-muted/80 transition-colors"
          >
            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-accent" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-foreground">Documentation</h4>
              <p className="text-sm text-muted-foreground">Browse our guides and tutorials</p>
            </div>
            <ExternalLink className="w-4 h-4 text-muted-foreground" />
          </a>

          <div className="border-t border-border pt-4 mt-4">
            <h4 className="font-medium text-foreground mb-3">Frequently Asked Questions</h4>
            <div className="space-y-4 text-sm">
              <div>
                <p className="font-medium text-foreground">How do early payment discounts work?</p>
                <p className="text-muted-foreground mt-1">
                  When you upload an invoice, DiscoRun AI calculates an optimal discount based on
                  your APR rate and the time until the payment is due. The earliest you can pay is
                  determined by your Early Payment Minimum setting. We email the supplier an offer
                  to accept a slightly reduced payment in exchange for getting paid sooner. If they
                  accept, you save money; if they decline, you pay the full amount on the original
                  due date.
                </p>
              </div>
              <div>
                <p className="font-medium text-foreground">How is the discount rate calculated?</p>
                <p className="text-muted-foreground mt-1">
                  The discount is based on the APR you set and the number of days between the early
                  payment date and the original due date. Formula: Discount = Invoice Amount × (APR
                  ÷ 100) × (Days Early ÷ 365). For example, a $10,000 invoice paid 30 days early at
                  12% APR saves approximately $98.63.
                </p>
              </div>
              <div>
                <p className="font-medium text-foreground">What happens if a supplier declines?</p>
                <p className="text-muted-foreground mt-1">
                  The invoice is marked as "No Agreement" and you simply pay the full amount on the
                  original due date. There's no penalty or obligation — it's entirely optional for
                  suppliers.
                </p>
              </div>
              <div>
                <p className="font-medium text-foreground">How do I generate ACH files?</p>
                <p className="text-muted-foreground mt-1">
                  Once suppliers accept your early payment offers, those invoices become eligible
                  for ACH processing. Click the "Download ACH" button to generate a NACHA-formatted
                  file that you can upload to your bank for batch payment processing. This
                  automatically marks those invoices as paid.
                </p>
              </div>
              <div>
                <p className="font-medium text-foreground">What APR range should I use?</p>
                <p className="text-muted-foreground mt-1">
                  We recommend an APR between 8% and 20% as the optimal range. Lower rates may not
                  be attractive enough for suppliers, while higher rates reduce your savings. The
                  sweet spot depends on your industry and supplier relationships.
                </p>
              </div>
              <div>
                <p className="font-medium text-foreground">Can I manage multiple users?</p>
                <p className="text-muted-foreground mt-1">
                  Yes! Use the Users button in the header to invite team members, assign roles
                  (Admin, Manager, or Viewer), and manage platform access. Admins have full control,
                  Managers can process invoices, and Viewers have read-only access.
                </p>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
